import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { SoftwareMapLicense } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { Donut } from '../components/charts'
import { Card, ErrorBanner, Spinner } from '../components/common'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { nonLicenziato, NotaLicenzaSbom } from '../components/Sbom'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Inventario Software → Licenze dei software: the licences of the libraries
 * the SBOM found (CENSO SBOM), in families — permissive, weak copyleft,
 * strong copyleft, other, undeclared — with how many libraries and servers
 * use each; a row opens the libraries under it.
 */

type Categoria = SoftwareMapLicense['category']

const COLORI: Record<Categoria, string> = {
  permissive: 'var(--ok)',
  weak_copyleft: 'var(--accent)',
  strong_copyleft: 'var(--warn)',
  other: 'var(--error)',
  undeclared: 'var(--text-muted)',
}

function nomeCategoria(c: Categoria): string {
  switch (c) {
    case 'permissive': return t('Permissiva')
    case 'weak_copyleft': return t('Copyleft debole')
    case 'strong_copyleft': return t('Copyleft forte')
    case 'other': return t('Altra o proprietaria')
    default: return t('Non dichiarata')
  }
}

function spiegazione(c: Categoria): string {
  switch (c) {
    case 'permissive': return t('Si usa e si ridistribuisce citando l’autore (MIT, Apache, BSD…).')
    case 'weak_copyleft': return t('Le modifiche alla libreria vanno condivise, il resto dell’applicazione no (LGPL, MPL, EPL…).')
    case 'strong_copyleft': return t('Se l’applicazione è distribuita, va distribuita con la stessa licenza: da verificare con l’ufficio legale (GPL, AGPL…).')
    case 'other': return t('Licenza non classificata o proprietaria: da verificare.')
    default: return t('Il componente non dichiara una licenza: da verificare.')
  }
}

type LibreriaRiga = { ecosystem: string; name: string; version: string; servers: number; example_ci_id: string }

function Librerie({ licenza }: { licenza: string }) {
  const dati = useAsync(() => api.licenseComponents(licenza), [licenza])
  if (dati.loading && !dati.data) return <Spinner />
  const colonne: Column<LibreriaRiga>[] = [
    { key: 'name', header: t('Libreria'), mono: true, render: (c) => <Link to={`/cis/${c.example_ci_id}#sbom`}>{c.name}</Link> },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'ecosystem', header: t('Ecosistema') },
    { key: 'servers', header: t('Server'), type: 'number' },
  ]
  return (
    <div className="card__inset">
      <ErrorBanner error={dati.error} />
      <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(c, i) => `${c.ecosystem}:${c.name}@${c.version}:${i}`}
        conteggio={{ nome: t('librerie') }} />
    </div>
  )
}

export default function SoftwareLicenses() {
  const dati = useAsync(() => api.softwareMapLicenses(), [])
  const [categoria, setCategoria] = useState<Categoria | ''>('')
  const header = (
    <header className="page__header">
      <div>
        <h1 className="page__title">{t('Licenze dei software')}</h1>
        <p className="page__subtitle">
          {t('analizza dallo SBOM le licenze delle librerie presenti sui server e le classifica come permissive, copyleft debole, copyleft forte, proprietarie, altre o non dichiarate. Permette di individuare dove sono presenti componenti con obblighi di redistribuzione prima che diventino un rischio.')}
        </p>
      </div>
      <BottoniEsporta nome="censo-software-licenze" disabled={!dati.data} path={api.softwareLicensesExportPath} />
    </header>
  )
  if (dati.error && nonLicenziato(dati.error)) return <>{header}<Card><NotaLicenzaSbom /></Card></>
  const righe = (dati.data ?? []).filter((r) => !categoria || r.category === categoria)
  const perCategoria = (dati.data ?? []).reduce<Record<string, number>>((acc, r) => {
    acc[r.category] = (acc[r.category] ?? 0) + r.components
    return acc
  }, {})
  const colonne: Column<SoftwareMapLicense>[] = [
    { key: 'license', header: t('Licenza'), value: (r) => r.license || t('Non dichiarata'), mono: true },
    { key: 'category', header: t('Famiglia'), value: (r) => nomeCategoria(r.category),
      render: (r) => <span title={spiegazione(r.category)}>{nomeCategoria(r.category)}</span> },
    { key: 'components', header: t('Componenti'), value: (r) => r.components, filter: false },
    { key: 'libraries', header: t('Librerie diverse'), value: (r) => r.libraries, filter: false },
    { key: 'servers', header: t('Server'), value: (r) => r.servers, filter: false },
  ]
  return (
    <>
      {header}
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <>
          <div className="grid grid--2">
            <Card title={t('Componenti per famiglia di licenza')}>
              <Donut centreLabel={t('componenti')} selected={categoria || null}
                onSliceClick={(k) => setCategoria(categoria === k ? '' : (k as Categoria))}
                slices={(Object.keys(COLORI) as Categoria[]).map((k) => ({
                  key: k, label: nomeCategoria(k), value: perCategoria[k] ?? 0, color: COLORI[k] }))} />
            </Card>
            <Card title={t('Che cosa vuol dire')}>
              <ul>
                {(Object.keys(COLORI) as Categoria[]).map((k) => (
                  <li key={k}><strong>{nomeCategoria(k)}</strong>: {spiegazione(k)}{' '}
                    <small className="text-muted">({(perCategoria[k] ?? 0).toLocaleString(localeCorrente())})</small></li>
                ))}
              </ul>
            </Card>
          </div>
          <Card>
            <DataTable rows={righe} rowKey={(r) => r.license || '—'} columns={colonne}
              initialSort={{ key: 'components', dir: 'desc' }} conteggio={{ nome: t('licenze') }}
              paginazione={{ predefinita: 50 }} expand={(r) => <Librerie licenza={r.license} />} />
          </Card>
        </>
      )}
    </>
  )
}
